import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ExternalLink, History, RefreshCw } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { getCompanyEvents, getEventWhyItMatters } from "../../lib/api";
import { Panel, SectionHeading } from "../Panel";
import { SkeletonBlock } from "../LoadingSkeleton";

const FILTERS = [
  { id: "all", label: "All", matches: () => true },
  { id: "high", label: "High materiality", matches: (event) => event.materiality === "high" },
  { id: "earnings", label: "Earnings", matches: (event) => event.event_type === "earnings" },
  { id: "financial", label: "Financial", matches: (event) => event.event_type === "financial" },
  { id: "strategic", label: "Strategic", matches: (event) => event.event_type === "strategic" },
  { id: "regulatory", label: "Regulatory", matches: (event) => event.event_type === "regulatory" },
];

const MATERIALITY_TONES = {
  high: "border-amber-500/30 bg-amber-500/10 text-amber-300",
  medium: "border-indigo-500/25 bg-indigo-500/10 text-indigo-300",
  low: "border-white/10 bg-white/[0.03] text-zinc-400",
};

const formatEventDate = (value) => {
  const parsed = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).toUpperCase();
};

const formatEventType = (value) => value.replaceAll("_", " ");

export const WhatChanged = ({ ticker }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState("all");
  const [expandedId, setExpandedId] = useState(null);
  const [retryKey, setRetryKey] = useState(0);
  const [eventIntelligence, setEventIntelligence] = useState({});
  const requestSequence = useRef(0);
  const intelligenceCache = useRef(new Map());
  const intelligenceRequests = useRef(new Map());

  useEffect(() => {
    const requestId = ++requestSequence.current;
    const controller = new AbortController();
    const activeIntelligenceRequests = intelligenceRequests.current;
    setEvents([]);
    setLoading(true);
    setError(false);
    setFilter("all");
    setExpandedId(null);
    setEventIntelligence({});

    activeIntelligenceRequests.forEach((request) => request.abort());
    activeIntelligenceRequests.clear();

    getCompanyEvents(ticker, {}, controller.signal)
      .then((response) => {
        if (requestSequence.current !== requestId || controller.signal.aborted) return;
        if (!response || !Array.isArray(response.events)) throw new Error("Invalid events response");
        setEvents(response.events);
        setLoading(false);
      })
      .catch(() => {
        if (requestSequence.current !== requestId || controller.signal.aborted) return;
        setEvents([]);
        setError(true);
        setLoading(false);
      });

    return () => {
      controller.abort();
      activeIntelligenceRequests.forEach((request) => request.abort());
      activeIntelligenceRequests.clear();
    };
  }, [ticker, retryKey]);

  const requestEventIntelligence = (event, force = false) => {
    const cacheKey = `${ticker}:${event.id}`;
    const cached = intelligenceCache.current.get(cacheKey);
    if (!force && cached) {
      setEventIntelligence((current) => ({
        ...current,
        [cacheKey]: { status: "success", data: cached },
      }));
      return;
    }
    if (intelligenceRequests.current.has(cacheKey)) return;

    const controller = new AbortController();
    intelligenceRequests.current.set(cacheKey, controller);
    setEventIntelligence((current) => ({
      ...current,
      [cacheKey]: { status: "loading" },
    }));
    getEventWhyItMatters(ticker, event.id, controller.signal)
      .then((response) => {
        if (controller.signal.aborted) return;
        if (!response || response.event_id !== event.id) {
          throw new Error("Invalid event intelligence response");
        }
        intelligenceCache.current.set(cacheKey, response);
        setEventIntelligence((current) => ({
          ...current,
          [cacheKey]: { status: "success", data: response },
        }));
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setEventIntelligence((current) => ({
          ...current,
          [cacheKey]: { status: "error" },
        }));
      })
      .finally(() => {
        intelligenceRequests.current.delete(cacheKey);
      });
  };

  const visibleEvents = useMemo(() => {
    const selectedFilter = FILTERS.find((item) => item.id === filter) || FILTERS[0];
    return events.filter(selectedFilter.matches);
  }, [events, filter]);

  return (
    <Panel data-testid="company-what-changed" className="min-w-0 overflow-hidden p-5 sm:p-6">
      <SectionHeading
        kicker="Company Events"
        title="What changed"
        icon={History}
        right={!loading && !error && events.length > 0 ? (
          <span className="shrink-0 font-mono text-[10px] uppercase tracking-wider text-zinc-600">
            {events.length} verified
          </span>
        ) : null}
      />

      {loading && <EventsSkeleton />}

      {!loading && error && (
        <div data-testid="what-changed-error" className="flex min-h-40 flex-col items-center justify-center rounded-md border border-white/[0.07] bg-[#0B0C0E] px-5 py-8 text-center">
          <p className="text-sm font-medium text-zinc-300">Events are temporarily unavailable.</p>
          <p className="mt-1 text-xs text-zinc-500">The rest of this company research remains available.</p>
          <button
            type="button"
            onClick={() => setRetryKey((value) => value + 1)}
            className="lv-press mt-4 inline-flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 font-mono text-[11px] text-zinc-400 hover:border-white/20 hover:text-zinc-200"
          >
            <RefreshCw className="h-3 w-3" /> Retry
          </button>
        </div>
      )}

      {!loading && !error && events.length === 0 && (
        <div data-testid="what-changed-empty" className="flex min-h-40 flex-col items-center justify-center rounded-md border border-white/[0.07] bg-[#0B0C0E] px-5 py-8 text-center">
          <History className="h-5 w-5 text-zinc-600" strokeWidth={1.5} />
          <p className="mt-3 text-sm font-medium text-zinc-300">No verified company events have been recorded yet.</p>
          <p className="mt-1 max-w-md text-xs leading-relaxed text-zinc-500">
            This reflects LONGVIEW's current event coverage, not an assessment that nothing has changed at the company.
          </p>
        </div>
      )}

      {!loading && !error && events.length > 0 && (
        <>
          <div aria-label="Filter company events" className="mb-4 flex max-w-full flex-wrap gap-1.5">
            {FILTERS.map((item) => (
              <button
                key={item.id}
                type="button"
                aria-pressed={filter === item.id}
                onClick={() => setFilter(item.id)}
                className={`lv-press rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-wide ${
                  filter === item.id
                    ? "border-amber-500/30 bg-amber-500/10 text-amber-300"
                    : "border-white/[0.08] bg-[#0B0C0E] text-zinc-500 hover:border-white/15 hover:text-zinc-300"
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>

          {visibleEvents.length === 0 ? (
            <div data-testid="what-changed-filter-empty" className="rounded-md border border-white/[0.07] bg-[#0B0C0E] px-4 py-8 text-center">
              <p className="text-xs text-zinc-500">No recorded events match this filter.</p>
              <button type="button" onClick={() => setFilter("all")} className="lv-press mt-3 font-mono text-[10px] uppercase tracking-wider text-amber-400 hover:text-amber-300">
                View all events
              </button>
            </div>
          ) : (
            <div className="min-w-0 space-y-2.5">
              {visibleEvents.map((event) => (
                <EventRow
                  key={event.id}
                  event={event}
                  expanded={expandedId === event.id}
                  onToggle={() => setExpandedId((current) => current === event.id ? null : event.id)}
                  intelligence={eventIntelligence[`${ticker}:${event.id}`]}
                  onRequestIntelligence={() => requestEventIntelligence(event)}
                  onRetryIntelligence={() => requestEventIntelligence(event, true)}
                />
              ))}
            </div>
          )}
        </>
      )}
    </Panel>
  );
};

const EventsSkeleton = () => (
  <div data-testid="what-changed-loading" className="space-y-3" aria-label="Loading company events">
    <div className="flex flex-wrap gap-2">
      <SkeletonBlock className="h-6 w-16" />
      <SkeletonBlock className="h-6 w-28" />
      <SkeletonBlock className="h-6 w-20" />
    </div>
    {[0, 1].map((item) => (
      <div key={item} className="rounded-md border border-white/[0.06] bg-[#0B0C0E] p-4">
        <SkeletonBlock className="h-3 w-36" />
        <SkeletonBlock className="mt-3 h-5 w-3/4" />
        <SkeletonBlock className="mt-2 h-3 w-full" />
        <SkeletonBlock className="mt-1.5 h-3 w-5/6" />
      </div>
    ))}
  </div>
);

const EventRow = ({
  event,
  expanded,
  onToggle,
  intelligence,
  onRequestIntelligence,
  onRetryIntelligence,
}) => (
  <motion.article
    initial={{ opacity: 0, y: 6 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.2 }}
    data-testid={`company-event-${event.id}`}
    className={`min-w-0 rounded-md border bg-[#0B0C0E] p-4 transition-colors ${
      event.materiality === "high" ? "border-amber-500/20" : "border-white/[0.07]"
    }`}
  >
    <div className="flex min-w-0 flex-wrap items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider">
      <time dateTime={event.event_date} className="text-zinc-500">{formatEventDate(event.event_date)}</time>
      <span aria-hidden="true" className="text-zinc-700">·</span>
      <span className="text-zinc-400">{formatEventType(event.event_type)}</span>
      <span className={`rounded border px-1.5 py-0.5 ${MATERIALITY_TONES[event.materiality] || MATERIALITY_TONES.low}`}>
        {event.materiality}
      </span>
    </div>

    <h3 className="mt-2 break-words text-sm font-semibold leading-snug text-white sm:text-[15px]">{event.headline}</h3>
    <p className={`mt-1.5 break-words text-xs leading-relaxed text-zinc-400 ${expanded ? "" : "line-clamp-2"}`}>
      {event.summary}
    </p>

    <AnimatePresence initial={false}>
      {expanded && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="min-w-0 overflow-hidden"
        >
          <div className="mt-3 border-t border-white/[0.07] pt-3 text-xs text-zinc-500">
            <span>Source: </span>
            <a
              href={event.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="break-all text-zinc-300 underline decoration-white/20 underline-offset-2 hover:text-amber-300"
            >
              {event.source_name} <ExternalLink className="inline h-3 w-3" aria-hidden="true" />
            </a>
          </div>
          <EventIntelligence
            intelligence={intelligence}
            onRequest={onRequestIntelligence}
            onRetry={onRetryIntelligence}
          />
        </motion.div>
      )}
    </AnimatePresence>

    <div className="mt-3 flex min-w-0 items-center justify-between gap-3 border-t border-white/[0.05] pt-2.5">
      <span className="min-w-0 truncate text-[11px] text-zinc-600">Source: {event.source_name}</span>
      <button
        type="button"
        aria-expanded={expanded}
        onClick={onToggle}
        className="lv-press flex shrink-0 items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-zinc-400 hover:text-amber-300"
      >
        {expanded ? "Hide details" : "View details"}
        <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`} />
      </button>
    </div>
  </motion.article>
);

const EventIntelligence = ({ intelligence, onRequest, onRetry }) => {
  if (!intelligence) {
    return (
      <button
        type="button"
        onClick={onRequest}
        className="lv-press mt-3 font-mono text-[10px] uppercase tracking-wider text-amber-400 hover:text-amber-300"
      >
        <span aria-hidden="true">✦</span> Why it matters
      </button>
    );
  }

  if (intelligence.status === "loading") {
    return (
      <div data-testid="event-intelligence-loading" className="mt-3 space-y-2 border-t border-white/[0.07] pt-3" aria-label="Loading event intelligence">
        <SkeletonBlock className="h-3 w-28" />
        <SkeletonBlock className="h-3 w-full" />
        <SkeletonBlock className="h-3 w-5/6" />
      </div>
    );
  }

  if (intelligence.status === "error") {
    return (
      <div data-testid="event-intelligence-error" className="mt-3 border-t border-white/[0.07] pt-3">
        <p className="text-xs text-zinc-500">Event intelligence is temporarily unavailable.</p>
        <button type="button" onClick={onRetry} className="lv-press mt-2 font-mono text-[10px] uppercase tracking-wider text-amber-400 hover:text-amber-300">
          Retry
        </button>
      </div>
    );
  }

  const analysis = intelligence.data;
  return (
    <div data-testid="event-intelligence" className="mt-3 min-w-0 border-t border-white/[0.07] pt-3">
      <div className="font-mono text-[10px] uppercase tracking-wider text-amber-400">
        <span aria-hidden="true">✦</span> Why it matters
      </div>
      <p className="mt-2 break-words text-sm leading-relaxed text-zinc-200">{analysis.why_it_matters}</p>
      <div className="mt-3 grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2">
        <IntelligenceNote label="Company context" text={analysis.company_context} />
        <IntelligenceNote label="LONGVIEW is watching" text={analysis.what_longview_is_watching} />
      </div>
      <p className="mt-3 break-words text-[11px] leading-relaxed text-zinc-600">{analysis.limitations}</p>
    </div>
  );
};

const IntelligenceNote = ({ label, text }) => (
  <div className="min-w-0">
    <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-600">{label}</div>
    <p className="mt-1 break-words text-xs leading-relaxed text-zinc-400">{text}</p>
  </div>
);
